import { useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  updateEmployee,
  selectEmployeesStatus,
  setEditingEmergencyContact,
  updateEditingEmergencyContact,
  clearEditingEmergencyContact,
  selectEditingEmergencyContact,
} from '../../slices/employees/employeesSlice';
import { Employee, EmergencyContact } from '../../models/employee.model';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

export function useEmergencyContactCardForm(employee: Partial<Employee>) {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [showAddSocialModal, setAddSocialShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);

  // Get editing state from Redux
  const editingEmergencyContact = useAppSelector(selectEditingEmergencyContact);
  const status = useAppSelector(selectEmployeesStatus);

  // Check if this emergency contact is being edited
  const isEdited = !!editingEmergencyContact && editingEmergencyContact.employee_id === employee.id;

  // Initialize the editing emergency contact in Redux if needed
  useEffect(() => {
    if (employee && employee.emergency_contact && !editingEmergencyContact && isEdited) {
      dispatch(
        setEditingEmergencyContact({
          ...employee.emergency_contact,
          employee_id: employee.id,
        })
      );
    }
  }, [employee, employee.emergency_contact, editingEmergencyContact, dispatch, isEdited]);

  const handleChange =
    <K extends keyof EmergencyContact>(field: K) =>
    (value: any) => {
      if (isEdited) {
        dispatch(updateEditingEmergencyContact({ field, value }));
      }
    };

  const cancelChanges = () => {
    dispatch(clearEditingEmergencyContact());
  };

  const saveChanges = async () => {
    if (isEdited && editingEmergencyContact && employee.id) {
      dispatch(showOverlay());
      await dispatch(
        updateEmployee({
          employeeId: employee.id,
          emergencyContactData: editingEmergencyContact,
          emergencyContactId: employee.emergency_contact?.id,
        })
      );
      dispatch(hideOverlay());
      setShowModal(false);
    }
  };

  const toggleEditing = () => {
    if (isEdited) {
      dispatch(clearEditingEmergencyContact());
    } else if (employee?.emergency_contact) {
      dispatch(
        setEditingEmergencyContact({
          ...employee.emergency_contact,
          employee_id: employee.id,
        })
      );
    } else {
      dispatch(
        setEditingEmergencyContact({
          full_name: '',
          email_address: '',
          postal_address: '',
          relationship: '',
          phone_number: '',
          employee_id: employee.id,
        })
      );
    }
  };

  const toogleEditingSocialModal = () => {
    setAddSocialShowModal((prev) => !prev);
  };

  const confirmExit = () => {
    dispatch(clearEditingEmergencyContact());
    setShowModal(false);
  };

  // Handle clicks outside the form
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEdited && formRef.current && !formRef.current.contains(event.target as Node)) {
        const targetElement = event.target as HTMLElement;

        if (
          targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
          targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
        ) {
          return;
        }

        setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEdited]);

  return {
    formData: editingEmergencyContact || employee.emergency_contact || {},
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    setShowModal,
    confirmExit,
    showAddSocialModal,
    setAddSocialShowModal,
    toogleEditingSocialModal,
    isLoading: status === 'loading',
    cancelChanges,
  };
}
